import { Router } from "@angular/router";
import { SocketService } from "./socket.service";
import { Injectable, NgZone } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { CartService } from "../cart-apis/cart.service";

@Injectable({
  providedIn: 'root'
})
export class OrderProcessingService {
  private socketResponseReceived = false;

  constructor(
    private socketService: SocketService,
    private ngZone: NgZone,
    private router: Router,
    private cartService: CartService,

  ) { }

  async processOrder(orderId: number, establishmentId: number, userId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      if (!this.socketService.isSocketConnected()) {
        await this.waitForSocketConnection();
      }
      const result = await this.emitOrderAndWaitResponse(orderId, establishmentId);

      if (result.success) {
        // Limpiar el carrito después de que la orden sea aceptada
        this.cartService.clearCart().subscribe({
          next: () => {
            // console.log('Cart cleared after order acceptance');
          },
          error: (error) => {
            console.error('Error clearing cart after order acceptance:', error);
          }
        });
        
        // Navegar a la página de órdenes
        await this.ngZone.run(() => {
          this.router.navigate(['/auth/orders']);
        });
        return {
          success: true,
          message: '¡Pago procesado exitosamente!'
        };
      } else {
        // Si la orden fue rechazada, NO navegar automáticamente
        // Dejar que el componente maneje la lógica del modal
        return {
          success: false,
          message: result.message || 'El establecimiento no aceptó tu orden en este momento'
        };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ?
        error.message :
        'Error al procesar la orden';
      return {
        success: false,
        message: errorMessage
      };
    }
  }

  private async waitForSocketConnection(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      // Si el socket ya está conectado, resolver inmediatamente
      if (this.socketService.isSocketConnected()) {
        resolve();
        return;
      }

      // Intentar conectar y esperar un tiempo razonable
      let attempts = 0;
      const maxAttempts = 10;
      const checkInterval = 500; // 500ms

      const checkConnection = () => {
        attempts++;
        if (this.socketService.isSocketConnected()) {
          resolve();
        } else if (attempts >= maxAttempts) {
          console.warn('Socket connection timeout, proceeding anyway');
          resolve(); // Resolver de todas formas para no bloquear el flujo
        } else {
          setTimeout(checkConnection, checkInterval);
        }
      };

      checkConnection();
    });
  }

  private async emitOrderAndWaitResponse(orderId: number, establishmentId: number): Promise<{success: boolean, message?: string}> {
    this.socketResponseReceived = false;

    return new Promise<{success: boolean, message?: string}>((resolve, reject) => {
      let timeoutId: NodeJS.Timeout;
      let isResolved = false;

      // Handler for incoming socket responses
      const handleResponse = async (data: any) => {
         // console.log('Received order response:', data);
         // console.log('Expected orderId:', orderId, 'Type:', typeof orderId);
         // console.log('Received orderId:', data?.orderId, 'Type:', typeof data?.orderId);

        if (!data) {
          console.warn('No data received in order response');
          return;
        }

        const orderIdStr = orderId.toString();

        // Handle array response format
        if (Array.isArray(data)) {
          data = data[0];
        }

        // Comparar tanto como string como número para asegurar compatibilidad
        const orderIdMatch = data.orderId === orderIdStr || data.orderId === orderId;
        // console.log('OrderId comparison result:', orderIdMatch, 'Expected:', orderIdStr, 'Received:', data.orderId);
        
        if (orderIdMatch) {
          this.socketResponseReceived = true;
          isResolved = true;

          if (timeoutId) {
            clearTimeout(timeoutId);
          }

          this.socketService.removeAllListeners('orderDelivery');

          // Convert response to boolean - only true if response is explicitly 1
          const isAccepted = data.response === 1;
           // console.log(`Order ${orderIdStr} ${isAccepted ? 'accepted' : 'rejected'}`);

          try {

          } catch (error) {
            console.error('Error sending establishment response:', error);
          }

          resolve({
            success: isAccepted,
            message: data.message || (isAccepted ? 'Pedido aceptado' : 'Pedido rechazado')
          });
        }
      };

      // Set up response listener
      this.socketService.onNewOrder(handleResponse);

      // Function to simulate a rejection response
      const simulateRejection = async () => {
        const rejectionData = {
          orderId: orderId.toString(),
          response: 0,
          message: 'Tiempo de espera agotado - El establecimiento no respondió'
        };

        // Call the handler with the rejection data
        handleResponse(rejectionData);
      };

      // Rest of the code remains the same...
      this.socketService.emitNewOrder(orderId, establishmentId)
        .catch((error) => {
          if (!isResolved) {
            console.error('Error emitting order:', error);
            this.socketService.removeAllListeners('orderDelivery');
            simulateRejection();
          }
        });

      timeoutId = setTimeout(() => {
        if (!isResolved) {
           // console.log(`No response received for order ${orderId} after timeout - simulating rejection`);
          simulateRejection();
        }
      }, 600000);
    });
  }
}
